// quiz-pricing.js
(function initQuizPricing() {
  window.CatQuiz = window.CatQuiz || {};
  CatQuiz.data = CatQuiz.data || {};

  function normalizeFoodCode(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  function normalizeVariantKey(value, fallback = "") {
    const normalized = normalizeFoodCode(value);
    return normalized || normalizeFoodCode(fallback);
  }

  function coercePositiveNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function buildFallbackFoodCatalog() {
    const variants = CatQuiz.config?.VARIANTS || {};
    const fallback = [];
    if (variants.huhn100g) {
      fallback.push({
        code: "huhn",
        uiKey: "huhn",
        label: "Huhn",
        variants: [
          {
            variantId: Number(variants.huhn100g),
            packSizeGrams: 100,
            key: "100g",
            label: "100g",
            isDefault: true,
            useForCart: true,
            isActive: true,
          }
        ],
        isActive: true,
      });
    }
    if (variants.huhnForelle100g) {
      fallback.push({
        code: "huhn_forelle",
        uiKey: "huhnForelle",
        label: "Huhn & Forelle",
        variants: [
          {
            variantId: Number(variants.huhnForelle100g),
            packSizeGrams: 100,
            key: "100g",
            label: "100g",
            isDefault: true,
            useForCart: true,
            isActive: true,
          }
        ],
        isActive: true,
      });
    }
    return fallback;
  }

  function normalizeCatalogVariant(food, variant, index) {
    const packSizeGrams = coercePositiveNumber(variant?.packSizeGrams || variant?.pack_size_g || food?.packSizeGrams || food?.pack_size_g, 100);
    const variantIdRaw = variant?.variantId ?? variant?.variant_id ?? variant?.id ?? null;
    const variantId = variantIdRaw == null || variantIdRaw === "" ? null : Number(variantIdRaw);
    const fallbackKey = `${food?.code || food?.uiKey || "food"}_${packSizeGrams}g_${index + 1}`;
    return {
      variantId: Number.isFinite(variantId) && variantId > 0 ? variantId : null,
      packSizeGrams,
      variantKey: normalizeVariantKey(variant?.variantKey || variant?.key || variant?.code || variant?.label, fallbackKey),
      variantLabel: String(variant?.variantLabel || variant?.label || `${packSizeGrams}g`).trim() || `${packSizeGrams}g`,
      isActive: variant?.isActive !== false && variant?.enabled !== false,
      isDefault: variant?.isDefault === true || variant?.default === true,
      useForCart: variant?.useForCart === true || variant?.isCartDefault === true,
    };
  }

  function normalizeCatalogFood(food) {
    if (!food) return null;
    const code = normalizeFoodCode(food?.code || food?.uiKey);
    if (!code) return null;

    const rawVariants = Array.isArray(food?.variants) && food.variants.length
      ? food.variants
      : [
          {
            variantId: food?.variantId ?? null,
            packSizeGrams: food?.packSizeGrams ?? null,
            key: food?.variantKey || null,
            label: food?.variantLabel || null,
            isDefault: true,
            useForCart: true,
            isActive: food?.isActive !== false,
          }
        ].filter((entry) => entry.variantId != null || entry.packSizeGrams != null);

    const variants = rawVariants
      .map((variant, index) => normalizeCatalogVariant({ ...food, code }, variant, index))
      .filter(Boolean);

    const activeVariants = variants.filter((variant) => variant.isActive !== false);
    const cartVariant =
      activeVariants.find((variant) => variant.useForCart) ||
      activeVariants.find((variant) => variant.isDefault) ||
      activeVariants[0] ||
      variants.find((variant) => variant.useForCart) ||
      variants.find((variant) => variant.isDefault) ||
      variants[0] ||
      null;

    return {
      ...food,
      code,
      uiKey: String(food?.uiKey || food?.code || code).trim() || code,
      label: String(food?.label || food?.name || food?.uiKey || code).trim() || code,
      isActive: food?.isActive !== false,
      variants,
      variantId: cartVariant?.variantId ?? null,
      variantKey: cartVariant?.variantKey ?? null,
      variantLabel: cartVariant?.variantLabel ?? null,
      packSizeGrams: cartVariant?.packSizeGrams ?? coercePositiveNumber(food?.packSizeGrams || food?.pack_size_g, 100),
    };
  }

  const FOOD_CATALOG = (Array.isArray(CatQuiz.config?.FOOD_CATALOG) && CatQuiz.config.FOOD_CATALOG.length
    ? CatQuiz.config.FOOD_CATALOG
    : buildFallbackFoodCatalog())
    .map(normalizeCatalogFood)
    .filter(Boolean);

  function getCatalogFood(ref) {
    const needle = normalizeFoodCode(ref);
    return FOOD_CATALOG.find((food) => normalizeFoodCode(food?.code) === needle || normalizeFoodCode(food?.uiKey) === needle) || null;
  }

  function resolveCatalogVariant(foodLike, catalogFood) {
    const source = foodLike || {};
    const food = catalogFood || getCatalogFood(source.code || source.uiKey);
    const variants = Array.isArray(food?.variants) ? food.variants : [];
    if (!variants.length) {
      const fallbackPack = coercePositiveNumber(source.packSizeGrams || food?.packSizeGrams, 100);
      return {
        variantId: source.variantId ?? food?.variantId ?? null,
        variantKey: normalizeVariantKey(source.variantKey || food?.variantKey, `${food?.code || source.code || source.uiKey || 'food'}_${fallbackPack}g`),
        variantLabel: String(source.variantLabel || food?.variantLabel || `${fallbackPack}g`).trim() || `${fallbackPack}g`,
        packSizeGrams: fallbackPack,
        isActive: true,
        isDefault: true,
        useForCart: true,
      };
    }

    const explicitVariantId = String(source.variantId ?? source.variant_id ?? "").trim();
    const explicitVariantKey = normalizeVariantKey(source.variantKey || source.variant_key || "");
    const explicitPackSize = coercePositiveNumber(source.packSizeGrams || source.pack_size_g, 0);

    return (
      variants.find((variant) => explicitVariantId && String(variant.variantId) === explicitVariantId) ||
      variants.find((variant) => explicitVariantKey && variant.variantKey === explicitVariantKey) ||
      variants.find((variant) => explicitPackSize > 0 && Number(variant.packSizeGrams) === explicitPackSize) ||
      variants.find((variant) => variant.useForCart) ||
      variants.find((variant) => variant.isDefault) ||
      variants.find((variant) => variant.isActive !== false) ||
      variants[0]
    );
  }

  function normalizePlanFood(food) {
    if (!food) return null;
    const catalogFood = getCatalogFood(food.code || food.uiKey);
    const code = catalogFood?.code || normalizeFoodCode(food.code || food.uiKey);
    if (!code) return null;
    const variant = resolveCatalogVariant(food, catalogFood);
    const packSizeGrams = coercePositiveNumber(food.packSizeGrams || variant?.packSizeGrams || catalogFood?.packSizeGrams, 100);
    const gramsPerDay = Number(food?.gramsPerDay || 0) || 0;
    const grams = food?.grams == null ? null : (Number(food.grams || 0) || 0);
    let units = food?.units == null ? null : (Number(food.units || 0) || 0);
    if (grams != null && packSizeGrams > 0) {
      units = grams > 0 ? Math.ceil(grams / packSizeGrams) : 0;
    }
    return {
      ...food,
      code,
      uiKey: catalogFood?.uiKey || food.uiKey || null,
      label: catalogFood?.label || food.label || code,
      variantId: variant?.variantId ?? food.variantId ?? catalogFood?.variantId ?? null,
      variantKey: variant?.variantKey ?? food.variantKey ?? catalogFood?.variantKey ?? null,
      variantLabel: variant?.variantLabel ?? food.variantLabel ?? catalogFood?.variantLabel ?? null,
      packSizeGrams,
      gramsPerDay,
      grams,
      units,
    };
  }

  function normalizePlanFoods(foods) {
    return (Array.isArray(foods) ? foods : []).map(normalizePlanFood).filter(Boolean);
  }

  const PRICING = CatQuiz.data.PRICING || (CatQuiz.data.PRICING = {
    currency: "EUR",
    discounts: { trialBox: 0.60 },
    variantsByCode: {},
    variantsById: {},
    variantsByKey: {},
  });

  PRICING.currency = PRICING.currency || "EUR";
  PRICING.discounts = (PRICING.discounts && typeof PRICING.discounts === "object")
    ? PRICING.discounts
    : { trialBox: 0.60 };
  if (!Object.prototype.hasOwnProperty.call(PRICING.discounts, "trialBox")) {
    PRICING.discounts.trialBox = 0.60;
  }
  PRICING.variantsByCode = (PRICING.variantsByCode && typeof PRICING.variantsByCode === "object") ? PRICING.variantsByCode : {};
  PRICING.variantsById = (PRICING.variantsById && typeof PRICING.variantsById === "object") ? PRICING.variantsById : {};
  PRICING.variantsByKey = (PRICING.variantsByKey && typeof PRICING.variantsByKey === "object") ? PRICING.variantsByKey : {};

  function hydrateStaticPricingFallbacks() {
    const legacyVariants = (PRICING.variants && typeof PRICING.variants === "object") ? PRICING.variants : {};
    FOOD_CATALOG.forEach((food) => {
      const code = normalizeFoodCode(food?.code || food?.uiKey);
      const variants = Array.isArray(food?.variants) && food.variants.length ? food.variants : [food];
      variants.forEach((variant) => {
        const variantId = String(variant?.variantId || "");
        const variantKey = String(variant?.variantKey || "");
        const packSize = coercePositiveNumber(variant?.packSizeGrams || food?.packSizeGrams, 100);
        const candidates = [
          `${String(food?.uiKey || "").trim()}${packSize}g`,
          `${String(food?.code || "").replace(/[^a-z0-9]+/gi, "")}${packSize}g`,
          variantKey,
        ].filter(Boolean);

        let staticPrice = Number(PRICING?.variantsById?.[variantId]);
        if (!Number.isFinite(staticPrice)) staticPrice = Number(PRICING?.variantsByKey?.[variantKey]);
        if (!Number.isFinite(staticPrice) && (variant?.useForCart || variant?.isDefault || variants.length === 1)) {
          staticPrice = Number(PRICING?.variantsByCode?.[code]);
        }
        if (!Number.isFinite(staticPrice)) {
          for (const key of candidates) {
            const value = Number(legacyVariants?.[key]);
            if (Number.isFinite(value)) {
              staticPrice = value;
              break;
            }
          }
        }

        if (Number.isFinite(staticPrice)) {
          if (variantId) PRICING.variantsById[variantId] = staticPrice;
          if (variantKey) PRICING.variantsByKey[variantKey] = staticPrice;
          if (variant?.useForCart || variant?.isDefault || variants.length === 1) {
            PRICING.variantsByCode[code] = staticPrice;
          }
        }
      });
    });
  }

  hydrateStaticPricingFallbacks();

  let _pricingPromise = null;

  async function fetchVariantPriceCents(variantId) {
    const res = await fetch(`/variants/${variantId}.js?ts=${Date.now()}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store"
    });
    if (!res.ok) throw new Error(`Variant price fetch failed (${variantId}): ${res.status}`);
    const json = await res.json();
    const p = (json && json.price != null) ? json.price : null;
    const n = (typeof p === "number") ? p : parseInt(String(p), 10);
    return Number.isFinite(n) ? n : null;
  }

  function ensurePricingLoaded(opts = {}) {
    hydrateStaticPricingFallbacks();
    const force = !!opts.force || !!PRICING.forceHydrate;
    if (_pricingPromise && !force) return _pricingPromise;
    if (force) _pricingPromise = null;

    const activeVariants = FOOD_CATALOG.flatMap((food) => {
      const variants = Array.isArray(food?.variants) && food.variants.length ? food.variants : [];
      return variants
        .filter((variant) => variant && variant.variantId && variant.isActive !== false)
        .map((variant) => ({ food, variant }));
    });
    if (!activeVariants.length) return Promise.resolve(false);

    _pricingPromise = (async () => {
      const tasks = activeVariants.map(({ food, variant }) =>
        fetchVariantPriceCents(variant.variantId)
          .then((price) => {
            if (price != null) {
              PRICING.variantsById[String(variant.variantId)] = price;
              if (variant.variantKey) PRICING.variantsByKey[String(variant.variantKey)] = price;
              if (variant.useForCart || variant.isDefault) {
                PRICING.variantsByCode[normalizeFoodCode(food.code)] = price;
              }
            }
          })
          .catch(() => {})
      );

      await Promise.allSettled(tasks);
      PRICING._hydrated = true;
      PRICING._hydratedAt = Date.now();
      return activeVariants.every(({ food, variant }) =>
        Number.isFinite(PRICING.variantsById[String(variant.variantId)]) ||
        Number.isFinite(PRICING.variantsByKey[String(variant.variantKey || "")]) ||
        Number.isFinite(PRICING.variantsByCode[normalizeFoodCode(food.code)])
      );
    })();

    return _pricingPromise;
  }

  function formatEURFromCents(cents) {
    if (!Number.isFinite(cents)) return "–";
    return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
  }

  function getVariantPriceForFood(food) {
    const normalized = normalizePlanFood(food);
    if (!normalized) return NaN;
    const byId = Number(PRICING?.variantsById?.[String(normalized.variantId || "")]);
    if (Number.isFinite(byId)) return byId;
    const byKey = Number(PRICING?.variantsByKey?.[String(normalized.variantKey || "")]);
    if (Number.isFinite(byKey)) return byKey;
    const byCode = Number(PRICING?.variantsByCode?.[normalizeFoodCode(normalized.code || normalized.uiKey)]);
    if (Number.isFinite(byCode)) return byCode;
    return NaN;
  }

  function boxTotalCentsFromPlan(plan) {
    const foods = normalizePlanFoods(plan?.foods);
    let total = 0;
    for (const food of foods) {
      const price = getVariantPriceForFood(food);
      const units = Number(food?.units || 0);
      if (!Number.isFinite(price)) return NaN;
      total += price * units;
    }
    return total;
  }

  ensurePricingLoaded().catch(() => {});

  function getSubscriptionPricing({ base, trialPlan, trialDays }) {
    const foods = normalizePlanFoods(base?.foods);
    const trialFoods = normalizePlanFoods(trialPlan?.foods);
    const pricesOk = foods.length > 0 && foods.every((food) => Number.isFinite(getVariantPriceForFood(food)));
    if (!pricesOk) return { pricesOk: false };

    const raw = Number(PRICING?.discounts?.trialBox);
    let discountRate = 0;
    if (Number.isFinite(raw)) {
      if (raw > 1) discountRate = raw / 100;
      else discountRate = (raw <= 0.5) ? raw : (1 - raw);
    }
    discountRate = Math.max(0, Math.min(1, discountRate));
    const trialPayFactor = 1 - discountRate;

    const followupMode = (document.querySelector('[name="followup_plan"]')?.value === 'half') ? 'half' : 'full';
    const monthFactor = (followupMode === 'half') ? 0.5 : 1;

    const trialTotalRawCents = boxTotalCentsFromPlan({ foods: trialFoods });
    const trialTotalCents = Math.round(trialTotalRawCents * trialPayFactor);

    const baseDailyKgCents = foods.reduce((sum, food) => {
      const price = getVariantPriceForFood(food);
      const gramsPerDay = Number(food?.gramsPerDay || 0);
      const kgPrice = Math.round(price * (1000 / Number(food?.packSizeGrams || 100)));
      return sum + ((gramsPerDay / 1000) * kgPrice);
    }, 0);

    const trialPerDayCents = Math.round(baseDailyKgCents * trialPayFactor);
    const monthDailyKgCents = baseDailyKgCents * monthFactor;
    const monthPerDayCents = Math.round(monthDailyKgCents);
    const totalUnits = trialFoods.reduce((sum, food) => sum + Number(food?.units || 0), 0);

    return {
      pricesOk: true,
      followupMode,
      trialPayFactor,
      discountRate,
      trialDays,
      totalUnits,
      trialTotalCents,
      trialPerDayCents,
      monthPerDayCents,
      foods,
      trialFoods,
    };
  }

  CatQuiz.pricing = CatQuiz.pricing || {};
  const api = {
    fetchVariantPriceCents,
    ensurePricingLoaded,
    formatEURFromCents,
    boxTotalCentsFromPlan,
    getSubscriptionPricing,
    normalizePlanFood,
    normalizePlanFoods,
    resolveCatalogVariant,
  };

  window.fetchVariantPriceCents = fetchVariantPriceCents;
  window.ensurePricingLoaded = ensurePricingLoaded;
  window.formatEURFromCents = window.formatEURFromCents || formatEURFromCents;
  window.boxTotalCentsFromPlan = boxTotalCentsFromPlan;
  window.getSubscriptionPricing = getSubscriptionPricing;
  Object.assign(CatQuiz.pricing, api);

  try { ensurePricingLoaded().catch(() => {}); } catch {}
})();
